import type { AnnotationShape, AnnotationTool } from '../types';

export const FREEFORM_STROKE_WIDTH = 3;
export const DEFAULT_TEXT_FONT_SIZE_PX = 16;
export const DEFAULT_TEXT_PADDING_PX = 8;

// Default shape sizes in normalized canvas units.
// Used when inserting a shape via single-click (2.5.7) or keyboard (2.1.1)
// without a drag — pick something visible but not so large it dominates.
const DEFAULT_BOX_W = 0.2;
const DEFAULT_BOX_H = 0.15;
const DEFAULT_CIRCLE_RX = 0.1;
const DEFAULT_CIRCLE_RY = 0.075;
const DEFAULT_LINE_LEN = 0.2;
const DEFAULT_ARROW_LEN = 0.2;
const DEFAULT_TEXT_W = 0.2;
const DEFAULT_TEXT_H = 0.06;

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Create a default-sized shape of the given tool centred on (cx, cy) in
 * normalized [0..1] canvas coordinates. Returns `null` for tools that
 * have no meaningful default (eraser, freeform — freeform requires real
 * drag input to be useful).
 */
export function createDefaultShape(
  tool: AnnotationTool,
  color: string,
  cx: number,
  cy: number,
  zoom = 1,
): AnnotationShape | null {
  const id = generateId();
  switch (tool) {
    case 'box':
      return {
        id,
        type: 'box',
        color,
        x: cx - DEFAULT_BOX_W / 2,
        y: cy - DEFAULT_BOX_H / 2,
        width: DEFAULT_BOX_W,
        height: DEFAULT_BOX_H,
      };
    case 'circle':
      return {
        id,
        type: 'circle',
        color,
        cx,
        cy,
        rx: DEFAULT_CIRCLE_RX,
        ry: DEFAULT_CIRCLE_RY,
      };
    case 'line':
      return {
        id,
        type: 'line',
        color,
        x1: cx - DEFAULT_LINE_LEN / 2,
        y1: cy,
        x2: cx + DEFAULT_LINE_LEN / 2,
        y2: cy,
      };
    case 'arrow':
      return {
        id,
        type: 'arrow',
        color,
        x1: cx - DEFAULT_ARROW_LEN / 2,
        y1: cy,
        x2: cx + DEFAULT_ARROW_LEN / 2,
        y2: cy,
      };
    case 'text':
      return {
        id,
        type: 'text',
        color,
        x: cx - DEFAULT_TEXT_W / 2,
        y: cy - DEFAULT_TEXT_H / 2,
        width: DEFAULT_TEXT_W,
        height: DEFAULT_TEXT_H,
        text: '',
        fontSize: DEFAULT_TEXT_FONT_SIZE_PX / (zoom || 1),
      };
    case 'freeform':
    case 'eraser':
    default:
      return null;
  }
}

/** Human-friendly label for a shape, for status announcements / aria-label. */
export function describeShape(shape: AnnotationShape): string {
  switch (shape.type) {
    case 'box':
      return `Box at ${Math.round(shape.x * 100)}%, ${Math.round(shape.y * 100)}%`;
    case 'circle':
      return `Circle at ${Math.round(shape.cx * 100)}%, ${Math.round(shape.cy * 100)}%`;
    case 'line':
      return `Line`;
    case 'arrow':
      return `Arrow`;
    case 'freeform':
      return `Freeform drawing`;
    case 'text':
      return shape.text ? `Text: ${shape.text.slice(0, 40)}` : `Empty text box`;
    default:
      return 'Shape';
  }
}
